/**
 * VEDANG JYOTISHAM V6.1 — ADVANCED VARSHAPHALA (TAJIKA ANNUAL SOLAR RETURN) MODULE
 * UI Component for annual solar return analysis, Muntha, Varshesha, and Tajika aspects.
 * Complies with Human-First Language Governance and canonical V6 visual palette.
 */

import React, { useState } from 'react';
import { calculateAdvancedVarshaphala, VarshaphalaResult } from '../utils/varshaphalaEngine';
import { Language } from '../types/kundali';
import { Calendar, Sun, Shield, Sparkles, BookOpen, CheckCircle2, Clock } from 'lucide-react';

interface VarshaphalaModuleProps {
  lang?: Language;
}

export const VarshaphalaModule: React.FC<VarshaphalaModuleProps> = ({ lang = 'en' }) => {
  const isHi = lang === 'hi';
  const [targetYear, setTargetYear] = useState<number>(2026);
  const [birthDate, setBirthDate] = useState<string>('1990-05-15');
  const [result, setResult] = useState<VarshaphalaResult>(() => calculateAdvancedVarshaphala('1990-05-15', 2026));

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setResult(calculateAdvancedVarshaphala(birthDate, targetYear));
  };

  return (
    <div className="space-y-6 px-4 py-6 bg-astro-bg">
      {/* Header Banner */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-astro-primary/10 border border-astro-primary/20 text-astro-primary font-bold flex items-center justify-center text-xl font-serif">
            <Sun className="w-6 h-6 text-astro-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-astro-text font-serif">
                {isHi ? 'उन्नत वर्षफल (ताजिका सौर वर्ष प्रणाली)' : 'Advanced Varshaphala (Tajika Annual Solar Return)'}
              </h2>
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2 py-0.5 rounded-md">
                {isHi ? 'ताजिका पद्धति' : 'Tajika Annual System'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'जन्मदिन से अगले जन्मदिन तक वार्षिक भविष्यफल, मुन्था, वर्षेश और ताजिक योग विश्लेषण'
                : 'Annual predictive framework from solar return moment to next birthday, Muntha, Varshesha & Tajika yogas.'}
            </p>
          </div>
        </div>
      </div>

      {/* Input Parameters Form */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-4">
        <h3 className="font-bold text-astro-primary text-base border-b border-astro-border pb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-astro-primary" /> {isHi ? 'वर्षफल गणना पैरामीटर' : 'Solar Return Parameters'}
        </h3>
        <form onSubmit={handleCalculate} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
              {isHi ? 'जन्म तिथि (Birth Date)' : 'Birth Date'}
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full bg-astro-bg border border-astro-border text-astro-text rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-astro-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
              {isHi ? 'लक्ष्य वर्ष (Target Year)' : 'Target Year (Solar Return)'}
            </label>
            <input
              type="number"
              value={targetYear}
              onChange={(e) => setTargetYear(parseInt(e.target.value) || 2026)}
              className="w-full bg-astro-bg border border-astro-border text-astro-text rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:border-astro-primary"
            />
          </div>
          <div>
            <button
              type="submit"
              className="w-full py-2.5 bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" /> {isHi ? 'वर्षफल कुंडली गणना करें' : 'Calculate Varshaphala'}
            </button>
          </div>
        </form>
      </div>

      {/* Varshaphala Result Dashboard */}
      {result && (
        <div className="space-y-6">
          {/* Executive Verdict Banner */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-astro-border pb-4">
              <div>
                <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-bg border border-astro-border px-2.5 py-0.5 rounded-full">
                  {isHi ? `वर्ष ${result.targetYear} का भविष्यफल` : `Solar Return Year ${result.targetYear}`}
                </span>
                <h3 className="text-xl font-bold text-astro-text font-serif mt-1">
                  {result.annualVerdict.overallMeaning}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">
                  <strong>Solar Return Moment:</strong> {result.solarReturnTimestamp} • <strong>Annual Ascendant:</strong> {result.annualAscendant.sign} ({result.annualAscendant.degree}°)
                </p>
              </div>
              <div className="bg-astro-bg border border-astro-border p-4 rounded-xl text-center shrink-0">
                <span className="text-[10px] uppercase text-astro-muted block">{isHi ? 'वार्षिक अनुकूलता स्कोर' : 'Annual Score'}</span>
                <span className="text-2xl font-bold text-astro-primary font-mono">{result.annualVerdict.score}/100</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Muntha Card */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                <span className="text-[10px] uppercase font-bold text-astro-accent">{isHi ? 'मुन्था स्थिति' : 'Muntha Placement'}</span>
                <h4 className="text-base font-bold text-astro-text">House {result.muntha.house} ({result.muntha.sign})</h4>
                <p className="text-xs text-astro-secondary-text">Muntha Lord: <span className="font-semibold text-astro-primary">{result.muntha.munthaLord}</span></p>
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                  {result.muntha.status}
                </span>
              </div>

              {/* Varshesha Card */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                <span className="text-[10px] uppercase font-bold text-astro-accent">{isHi ? 'वर्षेश (Year Lord)' : 'Varshesha (Year Lord)'}</span>
                <h4 className="text-base font-bold text-astro-text">{result.varshesha.planet}</h4>
                <p className="text-xs text-astro-secondary-text">{result.varshesha.humanMeaning}</p>
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary font-medium">
                  Strength: {result.varshesha.strengthScore}%
                </span>
              </div>

              {/* Technical Basis Card */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                <span className="text-[10px] uppercase font-bold text-astro-accent">{isHi ? 'शास्त्रीय आधार' : 'Technical Basis'}</span>
                <p className="text-xs text-astro-secondary-text leading-relaxed">{result.annualVerdict.technicalBasis}</p>
                <p className="text-[11px] text-astro-muted italic">{result.annualVerdict.classicalDetail}</p>
              </div>
            </div>
          </div>

          {/* Tajika Yogas & Aspects */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-4">
            <h3 className="font-bold text-astro-primary text-base border-b border-astro-border pb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-astro-primary" /> {isHi ? 'ताजिक योग एवं ग्रह दृष्टियां' : 'Tajika Yogas & Planetary Aspects'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.tajikaAspects.map((asp, idx) => (
                <div key={idx} className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-astro-primary">{asp.aspectName}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">{asp.nature}</span>
                  </div>
                  <p className="text-xs font-medium text-astro-text">Planets: {asp.planets}</p>
                  <p className="text-xs text-astro-secondary-text">{asp.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Deferred Advanced Techniques Notice */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-4 text-xs text-astro-secondary-text flex items-center justify-between">
            <span className="flex items-center gap-2 font-medium">
              <Clock className="w-4 h-4 text-astro-accent" /> Advanced Saham calculations & Panchadhikari allocation:
            </span>
            <span className="text-astro-accent font-semibold">Strictly Deferred to Future Roadmap (Unverified speculative rules excluded)</span>
          </div>
        </div>
      )}
    </div>
  );
};
